import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import MemoryIcon from '@mui/icons-material/Memory';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import HttpOutlinedIcon from '@mui/icons-material/HttpOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import CloudQueueOutlinedIcon from '@mui/icons-material/CloudQueueOutlined';
import { EventBusy, Schedule } from '@mui/icons-material';
import { Container, Table, TableTitle } from '../components';
import SearchBar from '../components/SearchBar/SearchBar';
import Pagination from '../components/Pagination/Pagination';
import usePagination from '../components/Pagination/usePagination';
import { useOtaEventsStore } from '../features/otaEvents/state/use-ota-events-store';
import { BOOL_OPTIONS, CHANNEL_OPTIONS, DEFAULT_SORT, DEVICE_TYPE_OPTIONS, EVENT_TYPE_OPTIONS, INITIAL_VALUES, LIMIT } from '../features/otaEvents/constants';
import {
  formatAllowed,
  formatBool,
  formatBytes,
  formatChannel,
  formatDateTime,
  formatEventType,
  formatNumber,
  formatStatusCode,
  formatValue,
} from '../features/otaEvents/utils';

const CHANNEL_VALUES = new Set(CHANNEL_OPTIONS.map((option) => option.value));

const getSearchFromUrl = (searchParams) => {
  const channel = searchParams.get('channel') || '';

  return {
    ...INITIAL_VALUES,
    serial_number: searchParams.get('serial_number') || '',
    channel: CHANNEL_VALUES.has(channel) ? channel : '',
  };
};

const OtaEvents = () => {
  const { data, total, isLoading, error, fetchOtaEvents } = useOtaEventsStore();
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT });
  const totalPages = Math.ceil(total / limit);
  const [urlParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(() => getSearchFromUrl(urlParams));
  const [sortBy, setSortBy] = useState(DEFAULT_SORT.sortBy);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT.sortOrder);

  useEffect(() => {
    fetchOtaEvents({ page, count: limit, searchParams: searchValue, sortBy, sortOrder });
  }, [page, limit, searchValue, sortBy, sortOrder]);

  const handleSearch = useCallback(
    (value, name) => {
      setSearchValue((prev) => ({ ...prev, [name]: value ?? '' }));
      if (page !== 1) updatePageUrl(1);
    },
    [page, updatePageUrl]
  );

  const handleSort = useCallback(
    (newSortBy, newSortOrder) => {
      setSortBy(newSortBy || '');
      setSortOrder(newSortOrder || '');
      if (page !== 1) updatePageUrl(1);
    },
    [page, updatePageUrl]
  );

  const inputs = useMemo(
    () => [
      {
        type: 'simpleSearch',
        placeholder: 'Поиск по серийному номеру...',
        defaultValue: searchValue.serial_number,
        handleSearch: (value) => handleSearch(value, 'serial_number'),
        prefix: <MemoryIcon fontSize={'small'} className="text-gray-500" />,
      },
      {
        type: 'simpleSearch',
        placeholder: 'Модель контроллера',
        defaultValue: searchValue.device_model,
        handleSearch: (value) => handleSearch(value, 'device_model'),
        prefix: <CategoryOutlinedIcon fontSize={'small'} className="text-gray-500" />,
      },
      {
        type: 'selector',
        placeholder: 'Семейство устройств',
        options: DEVICE_TYPE_OPTIONS,
        defaultValue: searchValue.device_type,
        onChange: (value) => handleSearch(value, 'device_type'),
        prefix: <DevicesOtherOutlinedIcon fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        type: 'filter',
        label: 'По типу события',
        options: EVENT_TYPE_OPTIONS,
        selectedOptions: searchValue.event_type,
        setSelectedOptions: (value) => handleSearch(value, 'event_type'),
        prefix: <SystemUpdateAltOutlinedIcon fontSize={'small'} className="text-gray-500" />,
      },
      {
        type: 'simpleSearch',
        placeholder: 'Файл прошивки',
        defaultValue: searchValue.firmware_file,
        handleSearch: (value) => handleSearch(value, 'firmware_file'),
        prefix: <InsertDriveFileOutlinedIcon fontSize={'small'} className="text-gray-500" />,
      },
      {
        type: 'simpleSearch',
        placeholder: 'Обновление с версии',
        defaultValue: searchValue.from_version,
        handleSearch: (value) => handleSearch(value, 'from_version'),
        prefix: <ArrowUpwardIcon fontSize={'small'} className="text-gray-500" />,
      },
      {
        type: 'simpleSearch',
        placeholder: 'Обновление на версию',
        defaultValue: searchValue.to_version,
        handleSearch: (value) => handleSearch(value, 'to_version'),
        prefix: <UpgradeIcon fontSize={'small'} className="text-gray-500" />,
      },
      {
        type: 'simpleSearch',
        placeholder: 'HTTP-статус',
        defaultValue: searchValue.status_code,
        handleSearch: (value) => handleSearch(value, 'status_code'),
        prefix: <HttpOutlinedIcon fontSize={'small'} className="text-gray-500" />,
      },
      {
        type: 'selector',
        placeholder: 'Было предложено обновление',
        options: BOOL_OPTIONS,
        defaultValue: searchValue.has_new,
        onChange: (value) => handleSearch(value, 'has_new'),
        prefix: <NewReleasesOutlinedIcon fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        type: 'selector',
        placeholder: 'Обновление разрешено',
        options: BOOL_OPTIONS,
        defaultValue: searchValue.allowed,
        onChange: (value) => handleSearch(value, 'allowed'),
        prefix: <LockOpenIcon fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        type: 'selector',
        placeholder: 'Канал прошивки',
        options: CHANNEL_OPTIONS,
        defaultValue: searchValue.channel,
        onChange: (value) => handleSearch(value, 'channel'),
        prefix: <CloudQueueOutlinedIcon fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        type: 'selector',
        placeholder: 'Только события с ошибкой',
        options: BOOL_OPTIONS,
        defaultValue: searchValue.only_errors,
        onChange: (value) => handleSearch(value, 'only_errors'),
        prefix: <ReportProblemOutlined fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        type: 'date',
        placeholderText: 'Начало периода',
        selected: searchValue.from,
        onChange: (value) => handleSearch(value, 'from'),
        prefix: <Schedule fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        type: 'date',
        placeholderText: 'Конец периода',
        selected: searchValue.to,
        onChange: (value) => handleSearch(value, 'to'),
        prefix: <EventBusy fontSize={'small'} className="text-gray-500 mr-2" />,
      },
    ],
    [handleSearch, searchValue]
  );

  const columns = useMemo(
    () => [
      { key: 'serial_number', title: 'Серийный номер', sortable: true, render: formatValue },
      { key: 'device_model', title: 'Модель', render: formatValue },
      { key: 'device_type', title: 'Семейство', render: formatValue },
      { key: 'event_type', title: 'Тип события', render: formatEventType },
      { key: 'firmware_file', title: 'Файл прошивки', render: (value) => <div className="min-w-[160px]">{formatValue(value)}</div> },
      { key: 'channel', title: 'Канал', render: formatChannel },
      { key: 'from_version', title: 'С версии', render: formatValue },
      { key: 'to_version', title: 'На версию', render: formatValue },
      { key: 'has_new', title: 'Есть обновление', render: formatBool },
      { key: 'allowed', title: 'Обновление разрешено', render: formatAllowed },
      { key: 'status_code', title: 'HTTP-статус', render: formatStatusCode },
      { key: 'duration_ms', title: 'Длительность, (мс)', sortable: true, render: formatNumber },
      { key: 'created_at', title: 'Время события', sortable: true, render: formatDateTime },
      {
        key: 'actions',
        title: 'Передано / размер',
        render: (row) => (
          <div className="min-w-max">
            {formatBytes(row?.bytes_sent)} / {formatBytes(row?.file_size)}
          </div>
        ),
      },
      {
        key: 'actions',
        title: 'Диапазон байт',
        render: (row) => <div className="min-w-max">{row?.range_end ? `${formatNumber(row?.range_start)} – ${formatNumber(row?.range_end)}` : '-'}</div>,
      },
      { key: 'hardware', title: 'Hardware', render: formatValue },
      { key: 'region', title: 'Регион', render: formatValue },
      { key: 'error', title: 'Ошибка', render: (value) => <div className="min-w-[180px]">{formatValue(value)}</div> },
      { key: 'endpoint', title: 'Endpoint', render: (value) => <div className="min-w-[160px] break-all">{formatValue(value)}</div> },
      { key: 'remote_addr', title: 'IP-адрес', render: (value) => <div className="min-w-max">{formatValue(value)}</div> },
      { key: 'user_agent', title: 'User-Agent', render: (value) => <div className="min-w-[160px]">{formatValue(value)}</div> },
    ],
    []
  );

  return (
    <Container>
      <div className="max-w-7xl mx-auto">
        <TableTitle title={'Журнал OTA-событий'} />
        <SearchBar inputs={inputs} />
      </div>
      <Table columns={columns} data={data ?? []} loading={isLoading} error={error} sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} rowKey="id">
        {isLoading || error || totalPages === 0 ? null : <Pagination totalPages={totalPages} withLimit={true} initialLimit={LIMIT} />}
      </Table>
    </Container>
  );
};

export default OtaEvents;
